import { DeleteOutlined, ExclamationCircleOutlined, InboxOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, InputNumber, Modal, Row, Select, Space } from 'antd';
import Dragger from 'antd/lib/upload/Dragger';
import { useCallback, useEffect, useState } from 'react';
import { Area, Risk, RiskCategory, Smat, SystemSetting, User } from '../../models';
import { apiClient, apiErrorMessage, toLowerTr } from '../../utils';

export function RiskEditPage(props: { smat?: Smat; risk?: Risk; onSaved: (risk?: Risk) => void }) {
  const [loading, setLoading] = useState(false);
  const [areasLoading, setAreasLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [riskCategoriesLoading, setRiskCategoriesLoading] = useState(false);
  const [areas, setAreas] = useState<Area[]>();
  const [users, setUsers] = useState<User[]>();
  const [riskCategories, setRiskCategories] = useState<RiskCategory[]>();
  const [systemSettingsLoading, setSystemSettingsLoading] = useState(false);
  const [probabilityOptions, setProbabilityOptions] = useState<number[]>();
  const [severityOptions, setSeverityOptions] = useState<number[]>();
  const [unsafeAction, setUnsafeAction] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<any[]>();
  const [documents, setDocuments] = useState(props.risk?.documents);
  const [toBeDeletedDocuments, setToBeDeletedDocuments] = useState<string[]>();

  const baseApiUrl = (process.env.REACT_APP_ISG_BASE_API_URL || window.location.protocol + '//' + window.location.host) + '/api/';

  const formValues: any = {
    ...props.risk,
    riskCategoryId: props.risk?.riskCategory?.id,
    areaId: props.risk?.area?.id,
    unsafeAction: props.risk?.unsafeAction ? 'true' : 'false',
    unsafeActionUserId: props.risk?.unsafeActionUser?.id,
  };

  const loadAreas = useCallback(() => {
    if (props.smat) {
      return;
    }

    setAreasLoading(true);
    apiClient
      .get('areas')
      .then(response => {
        setAreas(response.data);
        setAreasLoading(false);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
        setAreasLoading(false);
      });
  }, [props.smat]);

  useEffect(() => {
    loadUsers();
    loadRiskCategories();
    loadSystemSettings();
    loadAreas();
  }, [loadAreas]);

  useEffect(() => {
    if (props.risk?.unsafeAction) {
      setUnsafeAction(true);
    }
  }, [props.risk?.unsafeAction]);

  const loadUsers = () => {
    setUsersLoading(true);
    apiClient
      .get('users')
      .then(response => {
        setUsers(response.data);
        setUsersLoading(false);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
        setUsersLoading(false);
      });
  };

  const loadRiskCategories = () => {
    setRiskCategoriesLoading(true);
    apiClient
      .get('risk-categories')
      .then(response => {
        setRiskCategories(response.data);
        setRiskCategoriesLoading(false);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
        setRiskCategoriesLoading(false);
      });
  };

  const loadSystemSettings = () => {
    setSystemSettingsLoading(true);
    apiClient
      .get('system-settings')
      .then(response => {
        const systemSettings = response.data as SystemSetting[];

        const minProbabilityStr = systemSettings.find(m => m.id === 'MIN_PROBABILITY')?.value || '0';
        const maxProbabilityStr = systemSettings.find(m => m.id === 'MAX_PROBABILITY')?.value || '0';
        const minSeverityStr = systemSettings.find(m => m.id === 'MIN_SEVERITY')?.value || '0';
        const maxSeverityStr = systemSettings.find(m => m.id === 'MAX_SEVERITY')?.value || '0';

        const minProbability = parseInt(minProbabilityStr);
        const maxProbability = parseInt(maxProbabilityStr);
        const minSeverity = parseInt(minSeverityStr);
        const maxSeverity = parseInt(maxSeverityStr);

        const probabilityOptionValues: number[] = [];
        for (let i = minProbability; i <= maxProbability; i++) {
          probabilityOptionValues.push(i);
        }

        const severityOptionValues: number[] = [];
        for (let i = minSeverity; i <= maxSeverity; i++) {
          severityOptionValues.push(i);
        }

        setProbabilityOptions(probabilityOptionValues);
        setSeverityOptions(severityOptionValues);

        setSystemSettingsLoading(false);
      })
      .catch((reason: any) => {
        setSystemSettingsLoading(false);
        apiErrorMessage(reason);
      });
  };

  const onUnsafeActionChange = (value: any) => {
    setUnsafeAction(value === 'true');
  };

  const dummyUploadRequest = ({ onSuccess }: any) => {
    setTimeout(() => {
      onSuccess('ok');
    }, 0);
  };

  const normFile = (e: any) => {
    if (Array.isArray(e)) {
      return e;
    }
    setUploadedFiles(e.fileList.map((f: any) => f.originFileObj));
    return e && e.fileList;
  };

  const deleteDocument = (documentId: string) => {
    const newToBeDeletedList: string[] = toBeDeletedDocuments ? [...toBeDeletedDocuments] : [];
    newToBeDeletedList.push(documentId);
    setToBeDeletedDocuments(newToBeDeletedList);
    const newList = documents?.filter(d => d.id !== documentId);
    setDocuments(newList);
  };

  const confirmDeleteDocument = (id: string) => {
    const onOk = () => deleteDocument(id);
    Modal.confirm({
      title: 'Doküman sil',
      icon: <ExclamationCircleOutlined />,
      content: 'Dokümanı silmek istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Vazgeç',
      onOk: onOk,
    });
  };

  const onFinish = (values: any) => {
    const body = { ...values };
    if (props.smat) {
      body.smatId = props.smat.id;
      body.areaId = props.smat.area.id;
    }

    var formData = new FormData();

    if (uploadedFiles?.length) {
      for (let i = 0; i < uploadedFiles?.length; i++) {
        formData.append(`files[${i}]`, uploadedFiles[i]);
      }
    }

    if (toBeDeletedDocuments?.length) {
      for (let i = 0; i < toBeDeletedDocuments?.length; i++) {
        formData.append(`toBeDeletedDocumentIds[${i}]`, toBeDeletedDocuments[i]);
      }
    }

    for (let key of Object.keys(body)) {
      if (key !== 'dragger' && body[key]) {
        formData.append(key, body[key]);
      }
    }

    setLoading(true);
    if (!props.risk) {
      apiClient
        .post('risks', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then(() => {
          props.onSaved();
        })
        .catch((reason: any) => {
          apiErrorMessage(reason);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      apiClient
        .put(`risks/${props.risk?.id}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then((response: any) => {
          props.onSaved(response.data);
        })
        .catch((reason: any) => {
          apiErrorMessage(reason);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  return (
    <Form layout="vertical" name="basic" onFinish={onFinish} initialValues={formValues}>
      {!props.smat && (
        <Form.Item
          label="Alan"
          rules={[
            {
              required: true,
              message: 'Zorunlu alan',
            },
          ]}
          name="areaId"
        >
          <Select
            loading={areasLoading}
            showSearch
            filterOption={(input, option) => option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
          >
            {areas?.map(m => (
              <Select.Option key={m.id} value={m.id}>
                {m.code + ' - ' + m.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      )}

      <Form.Item
        label="Risk kategorisi"
        rules={[
          {
            required: true,
            message: 'Zorunlu alan',
          },
        ]}
        name="riskCategoryId"
      >
        <Select loading={riskCategoriesLoading}>
          {riskCategories?.map(m => (
            <Select.Option key={m.id} value={m.id}>
              {m.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item label="Görüşülen kişi sayısı" name="interviewCount">
        <InputNumber type="number" style={{ width: '100%' }} min={0} />
      </Form.Item>

      <Form.Item label="Güvensiz davranış mı?" name="unsafeAction">
        <Select onChange={onUnsafeActionChange}>
          <Select.Option key="true" value="true">
            Evet
          </Select.Option>
          <Select.Option key="false" value="false">
            Hayır
          </Select.Option>
        </Select>
      </Form.Item>

      {unsafeAction && (
        <Form.Item label="Güvensiz davranış isim" name="unsafeActionUserId">
          <Select
            loading={usersLoading}
            showSearch
            filterOption={(input, option) => (toLowerTr(option?.children)?.indexOf(toLowerTr(input) || input) || -1) >= 0}
          >
            {users?.map(m => (
              <Select.Option key={m.id} value={m.id}>
                {m.fullNameId}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      )}

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Olasılık"
            name="beforeProbability"
            rules={[
              {
                required: true,
                message: 'Zorunlu alan',
              },
            ]}
          >
            <Select loading={systemSettingsLoading}>
              {probabilityOptions?.map(o => (
                <Select.Option key={'probability_' + o} value={o}>
                  {o}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Şiddet"
            name="beforeSeverity"
            rules={[
              {
                required: true,
                message: 'Zorunlu alan',
              },
            ]}
          >
            <Select loading={systemSettingsLoading}>
              {severityOptions?.map(o => (
                <Select.Option key={'severity_' + o} value={o}>
                  {o}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        label="Risk ve fırsatların tanımı"
        name="riskDescription"
        rules={[
          { required: true, message: 'Zorunlu alan' },
          { max: 4000, message: 'En fazla 4000 karakter' },
        ]}
      >
        <Input.TextArea />
      </Form.Item>

      <Form.Item
        label="Karşı tedbir açıklaması / Fırsat"
        name="precautionDescription"
        rules={[
          { required: true, message: 'Zorunlu alan' },
          { max: 4000, message: 'En fazla 4000 karakter' },
        ]}
      >
        <Input.TextArea />
      </Form.Item>

      <Form.Item label="Dokümanlar">
        {!!documents && documents?.length > 0 && (
          <Space direction="vertical" style={{ marginBottom: '1rem', width: '100%' }}>
            {documents.map(d => (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <a href={`${baseApiUrl}risks/${props.risk?.id}/documents/${d.id}`}>{d.path}</a>
                <DeleteOutlined onClick={() => confirmDeleteDocument(d.id)} />
              </div>
            ))}
          </Space>
        )}
        <Form.Item
          name="dragger"
          valuePropName="fileList"
          getValueFromEvent={normFile}
          noStyle
          rules={[{ required: documents?.length ? false : true, message: 'Risk için en az bir doküman eklenmelidir.' }]}
        >
          <Dragger name="files" multiple={true} customRequest={dummyUploadRequest}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Bir dosya sürükleyin veya tıklayın</p>
          </Dragger>
        </Form.Item>
      </Form.Item>

      <Form.Item style={{ marginTop: '2rem' }}>
        <Button type="primary" htmlType="submit" loading={loading}>
          Gönder
        </Button>
      </Form.Item>
    </Form>
  );
}
